// StressCountBadge - Positioned stress count badge component
// Displays per-line stress count (stressed/total) with match-quality coloring
// Uses design tokens from docs/design/feedback_system/design_tokens.json
// Includes tooltip with word breakdown and guidance on hover/focus

import { memo, useState, useRef, useEffect, useId } from 'react'
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react'
import { generateTooltipContent, type WordBreakdown } from '../utils/stressTooltipContent'

interface StressCountBadgeProps {
  /** Number of stressed syllables in the line */
  stressedCount: number
  /** Total number of syllables in the line */
  totalCount: number
  /** Expected number of stressed syllables (when known) */
  expectedCount?: number
  /** Per-word syllable breakdown (optional) */
  words?: WordBreakdown[]
  /** X position relative to editor root */
  x: number
  /** Y position relative to editor root */
  y: number
  /** Node key for React key and testing */
  nodeKey: string
}

/**
 * Match quality determination logic.
 * Hides badge when counts match expectations perfectly.
 * Shows colored badge based on distance from expected count.
 */
function getMatchQuality(
  stressedCount: number,
  expectedCount: number | undefined
): 'match' | 'close' | 'needs-attention' | 'neutral' {
  // No expectations available - neutral state
  if (expectedCount === undefined) {
    return 'neutral'
  }

  // Perfect match - hide badge (criteria met)
  if (stressedCount === expectedCount) {
    return 'match'
  }

  // Close match - within 1 syllable
  const difference = Math.abs(stressedCount - expectedCount)
  if (difference <= 1) {
    return 'close'
  }

  // Needs attention - difference > 1
  return 'needs-attention'
}

/**
 * Get styling based on match quality.
 * Colors from design_tokens.json: semantic.feedback + neutral fallback
 */
function getStyleForQuality(quality: 'match' | 'close' | 'needs-attention' | 'neutral'): {
  color: string
  backgroundColor: string
  borderColor: string
} {
  switch (quality) {
    case 'match':
      // Matches - success.600 (#059669)
      return {
        color: '#059669',
        backgroundColor: '#f0fdf4',
        borderColor: '#059669',
      }
    case 'close':
      // Close - warm.600 (#d97706)
      return {
        color: '#d97706',
        backgroundColor: '#fffbf0',
        borderColor: '#d97706',
      }
    case 'needs-attention':
      // Needs attention - warm.700 (#b45309)
      return {
        color: '#b45309',
        backgroundColor: '#fef3d9',
        borderColor: '#b45309',
      }
    case 'neutral':
      // Neutral - neutral.400 text on neutral.100 bg
      return {
        color: '#6b7280',
        backgroundColor: '#f3f4f6',
        borderColor: '#d1d5db',
      }
  }
}

/**
 * Custom comparison to prevent unnecessary re-renders.
 * Badges update frequently during scroll/typing - prevent wasteful re-renders.
 */
function arePropsEqual(prev: StressCountBadgeProps, next: StressCountBadgeProps): boolean {
  // Deep comparison for words array
  // Both undefined/null counts as equal
  if (prev.words === next.words) {
    // Same reference (or both undefined)
  } else if (!prev.words || !next.words) {
    // One is defined, the other isn't
    return false
  } else if (prev.words.length !== next.words.length) {
    return false
  } else {
    // Both defined, same length - compare contents
    for (let i = 0; i < prev.words.length; i++) {
      const prevWord = prev.words[i]
      const nextWord = next.words[i]
      if (prevWord.word !== nextWord.word) return false
      if (prevWord.syllables.length !== nextWord.syllables.length) return false
      for (let j = 0; j < prevWord.syllables.length; j++) {
        if (prevWord.syllables[j].text !== nextWord.syllables[j].text) return false
        if (prevWord.syllables[j].stressed !== nextWord.syllables[j].stressed) return false
      }
    }
  }

  return (
    prev.stressedCount === next.stressedCount &&
    prev.totalCount === next.totalCount &&
    prev.expectedCount === next.expectedCount &&
    prev.x === next.x &&
    prev.y === next.y &&
    prev.nodeKey === next.nodeKey
  )
}

/**
 * StressCountBadge - Per-line stress count display with match-quality coloring and tooltip
 *
 * Design token reference: docs/design/feedback_system/design_tokens.json
 * - Badge padding: components.badge.statusBadge.padding (4px 8px)
 * - Badge font size: typography.fontSize.xs (12px)
 * - Badge font weight: typography.fontWeight.medium (500)
 * - Border radius: borderRadius.base (6px)
 * - Tooltip padding: components.tooltip.padding (8px 12px)
 * - Tooltip font size: typography.fontSize.sm (14px)
 * - Shadow: shadow.medium
 * - Colors: semantic.feedback.{matches, close, needs-attention} + neutral fallback
 *
 * Behavior:
 * - Hides when stressedCount === expectedCount (perfect match)
 * - Shows tooltip on hover/focus after 200ms delay
 * - Hides tooltip on mouse leave/blur
 * - Tooltip positioned with @floating-ui (offset + flip/shift)
 *
 * Accessibility:
 * - aria-describedby pointing to tooltip
 * - tabIndex={0} for keyboard focus
 * - role="tooltip" on tooltip element
 * - data-testid for test stability
 */
const StressCountBadge = memo(function StressCountBadge({
  stressedCount,
  totalCount,
  expectedCount,
  words,
  x,
  y,
  nodeKey,
}: StressCountBadgeProps) {
  // Tooltip state management - hooks must be called unconditionally
  const [showTooltip, setShowTooltip] = useState(false)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tooltipId = useId()

  // @floating-ui positioning
  const { refs, floatingStyles } = useFloating({
    placement: 'top',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  })

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current)
      }
    }
  }, [])

  // Compute quality and styles - after hooks, before early return
  const quality = getMatchQuality(stressedCount, expectedCount)

  // Hide badge when criteria are met (perfect match)
  if (quality === 'match') {
    return null
  }

  const { color, backgroundColor, borderColor } = getStyleForQuality(quality)

  // Generate tooltip content
  const tooltipContent = generateTooltipContent(
    stressedCount,
    totalCount,
    expectedCount,
    words
  )

  // Handle hover/focus with 200ms delay
  const handleMouseEnter = () => {
    hoverTimerRef.current = setTimeout(() => {
      setShowTooltip(true)
    }, 200)
  }

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    setShowTooltip(false)
  }

  const handleFocus = () => {
    hoverTimerRef.current = setTimeout(() => {
      setShowTooltip(true)
    }, 200)
  }

  const handleBlur = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    setShowTooltip(false)
  }

  // Build accessible label
  const label = expectedCount !== undefined
    ? `${stressedCount} stressed out of ${totalCount} syllables (expected ${expectedCount})`
    : `${stressedCount} stressed out of ${totalCount} syllables`

  return (
    <>
      <div
        ref={refs.setReference}
        className="stress-count-badge"
        style={{
          position: 'absolute',
          left: `${x}px`,
          top: `${y}px`,
          // Design token: statusBadge.padding (4px 8px)
          padding: '2px 4px',
          // Design token: typography.fontSize.xs (12px)
          fontSize: '10px',
          // Design token: typography.fontWeight.medium (500)
          fontWeight: 500,
          // Design token: borderRadius.base (6px)
          borderRadius: '6px',
          // Match-quality colors
          color,
          backgroundColor,
          border: `0.5px solid ${borderColor}`,
          // Layout
          whiteSpace: 'nowrap',
          pointerEvents: 'auto',
        }}
        aria-label={label}
        aria-describedby={showTooltip ? tooltipId : undefined}
        tabIndex={0}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        data-testid={`stress-count-badge-${nodeKey}`}
        data-quality={quality}
      >
        {stressedCount}/{totalCount}
      </div>

      {showTooltip && (
        <div
          ref={refs.setFloating}
          role="tooltip"
          id={tooltipId}
          style={{
            ...floatingStyles,
            // Design token: tooltip.padding (8px 12px)
            padding: '8px 12px',
            // Design token: tooltip.fontSize.sm (14px)
            fontSize: '14px',
            // Design token: borderRadius.base (6px)
            borderRadius: '6px',
            // Design token: tooltip.maxWidth (300px)
            maxWidth: '300px',
            // Design token: shadow.medium
            boxShadow: '0 4px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            // Colors from design tokens
            backgroundColor: '#ffffff',
            color: '#111827',
            border: '1px solid #e5e7eb',
            // Layout
            zIndex: 1000,
            pointerEvents: 'none',
          }}
          data-testid={`stress-count-tooltip-${nodeKey}`}
        >
          {/* Summary text */}
          <div
            style={{
              fontWeight: 500,
              marginBottom: tooltipContent.words || tooltipContent.guidance ? '8px' : 0,
            }}
            data-testid="tooltip-summary"
          >
            {tooltipContent.summary}
          </div>

          {/* Word breakdown (if available) */}
          {tooltipContent.words && tooltipContent.words.length > 0 && (
            <div
              style={{
                fontSize: '13px',
                color: '#6b7280',
                marginBottom: tooltipContent.guidance ? '8px' : 0,
                fontFamily: 'monospace',
              }}
              data-testid="tooltip-word-breakdown"
            >
              {tooltipContent.words.map((word, index) => (
                <div key={index} style={{ marginBottom: '2px' }}>
                  {word.display}
                </div>
              ))}
            </div>
          )}

          {/* Guidance (if applicable) */}
          {tooltipContent.guidance && (
            <div
              style={{
                fontSize: '13px',
                color: '#6b7280',
                fontStyle: 'italic',
              }}
              data-testid="tooltip-guidance"
            >
              {tooltipContent.guidance}
            </div>
          )}
        </div>
      )}
    </>
  )
}, arePropsEqual)

export default StressCountBadge
